import { useQuery } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

export interface OrganisationBranding {
  brandColour?: string | null;
  logoUrl?: string | null;
}

export interface OrganisationBrandingUrls {
  website?: string | null;
  appLink?: string | null;
}

export interface GetOrganisationBrandingResponse {
  organisationId: string;
  name: string;
  branding?: OrganisationBranding | null;
  urls?: OrganisationBrandingUrls | null;
}

export const organisationBrandingQueryKey = (organisationId: string) => [
  'organisation-branding',
  organisationId,
];

function getOrganisationBranding(organisationId: string) {
  return axiosPrivate.get<GetOrganisationBrandingResponse>(
    `/v3/pharmacist/organisations/${organisationId}/branding`
  );
}

export interface UseOrganisationBrandingResult {
  branding: OrganisationBranding | null;
  isLoading: boolean;
  isError: boolean;
}

// Branding is a convenience — the organisation's app brand colour and logo are used to preset
// the onboarding steps. A failure is deliberately silent (`silenceErrors` keeps the global
// query error toast away, no retry): the user simply picks the values themselves.
export function useOrganisationBranding(organisationId: string): UseOrganisationBrandingResult {
  const {
    data,
    isInitialLoading: isLoading,
    isError,
  } = useQuery({
    queryKey: organisationBrandingQueryKey(organisationId),
    queryFn: () => getOrganisationBranding(organisationId),
    enabled: !!organisationId,
    retry: false,
    meta: { silenceErrors: true },
  });

  return {
    branding: data?.data.branding ?? null,
    isLoading,
    isError,
  };
}
